import { addEntity, addComponent, defineQuery } from 'bitecs';
import { Plant, PLANT_TYPES, Tile, Position, Time } from '../world.js';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT } from '../config.js';
import { tileManager, TILE_TYPES, BIOME_TYPES } from './tileManager.js';
import { TICKS_PER_SECOND, TICKS_PER_DAY } from './time.js';

// Constants
const PLANT_DENSITY = 0.4; // Reduced from 0.7 for better performance
const WIND_INFLUENCE = 0.3; // How much wind affects seed dispersal
export const MIN_REPRODUCTION_ENERGY = 50; // Minimum energy needed to reproduce
const TICK_RATE = TICKS_PER_SECOND * 2; // Process plants every 2 game ticks for performance
const MAX_PLANTS_PER_CHUNK = 3; // Reduced from 5 to 3 plants per chunk
const CHUNK_SIZE = 4; // Size of chunks in tiles
const plantPositions = new Map(); // Track occupied positions
const MAX_PLANTS_PER_FRAME = 25; // Reduced from 50 to 25 plants per frame
const ACTIVE_RADIUS = 20; // Reduced from 30 to 20 chunks

// Growth rates (per day)
const GROWTH_RATE = 0.1; // 10% growth per day
const ENERGY_GAIN_RATE = 20; // 20% energy gain per day
const REGROWTH_MULTIPLIER = 0.4; // 40% regrowth per day

// Create queries
const landTileQuery = defineQuery([Tile]);
const plantQuery = defineQuery([Plant]);
const timeQuery = defineQuery([Time]);

// Plant type enum
const PLANT_TYPE = {
    BUSH: 1,
    MUSHROOM: 2,
    HERB: 3,
    CACTUS: 4,
    FLOWER: 5,
    PINE: 6
};

// Cache for plant types to avoid repeated lookups
const plantTypeCache = new Map();

function getPlantType(typeId) {
    if (!plantTypeCache.has(typeId)) {
        plantTypeCache.set(typeId, Object.values(PLANT_TYPES).find(type => type.id === typeId));
    }
    return plantTypeCache.get(typeId);
}

function canPlacePlant(tile, plantTypeId) {
    // Check if tile is land and has the right biome
    const biomeId = Tile.biome[tile]; // Get the numeric biome ID from the tile
    const plantType = getPlantType(plantTypeId);
    if (!plantType) return false;

    // Convert biome names in plantType.biomes to IDs for comparison
    const allowedBiomeIds = plantType.biomes.map(name => BIOME_TYPES[name]);

    return Tile.type[tile] === 1 && // Land tile
           allowedBiomeIds.includes(biomeId); // Check against numeric ID
}

function getRandomPlantType() {
    // 60% chance for pine trees, 40% chance for other plants
    if (Math.random() < 0.6) {
        return PLANT_TYPE.PINE;
    }
    
    // For the remaining 40%, distribute among other plant types
    const otherTypes = Object.values(PLANT_TYPES).filter(type => type.id !== PLANT_TYPE.PINE);
    const randomType = otherTypes[Math.floor(Math.random() * otherTypes.length)];
    return randomType.id;
}

// Helper function to calculate wind-influenced position
function getWindInfluencedPosition(baseX, baseY, radius) {
    // Simulate simple wind by biasing the direction
    const windAngle = Math.random() * Math.PI * 2; // Random wind direction
    const windStrength = Math.random() * WIND_INFLUENCE;
    
    const distance = Math.random() * radius;
    const angle = windAngle + (Math.random() - 0.5) * Math.PI * (1 - windStrength);
    
    return {
        x: Math.round(baseX + Math.cos(angle) * distance),
        y: Math.round(baseY + Math.sin(angle) * distance)
    };
}

// Helper function to get chunk key
function getChunkKey(x, y) {
    const chunkX = Math.floor(x / (CHUNK_SIZE * TILE_SIZE));
    const chunkY = Math.floor(y / (CHUNK_SIZE * TILE_SIZE));
    return `${chunkX},${chunkY}`;
}

// Helper function to count plants in chunk
function getPlantsInChunk(chunkKey) {
    let plants = 0;
    const positions = plantPositions.get(chunkKey) || new Set();
    return positions.size;
}

// Helper function to check if a position is valid for a new plant
function isValidPlantPosition(x, y, plantType) {
    // Check map boundaries
    if (x < 0 || x >= MAP_WIDTH || y < 0 || y >= MAP_HEIGHT) {
        return false;
    }

    // Get tile at position
    const tile = tileManager.getTile(x, y);
    if (!tile) return false;

    // Check if position is already occupied
    const posKey = `${x},${y}`;
    const chunkKey = getChunkKey(x * TILE_SIZE, y * TILE_SIZE);
    // NOTE: Using tile coordinates for getPlantsInChunk assumes TILE_SIZE is 1 for chunk calcs, which might be wrong.
    // Revisit chunk logic if TILE_SIZE != 1.
    // if (getPlantsInChunk(chunkKey) >= MAX_PLANTS_PER_CHUNK) {
    //     return false;
    // }

    // Get the numeric biome ID from the tile
    const biomeId = tile.biome; // Tile object already has biome ID
    if (!plantType) return false; // Check if plantType is valid

    // Convert biome names in plantType.biomes to IDs for comparison
    const allowedBiomeIds = plantType.biomes.map(name => BIOME_TYPES[name]);

    // Check if tile is suitable for this plant type
    return tile.type === TILE_TYPES.LAND && allowedBiomeIds.includes(biomeId);
}

// Helper function to attempt plant reproduction
function tryReproduction(world, plant) {
    const plantType = getPlantType(Plant.type[plant]);
    if (!plantType) return;
    
    // Check if plant can reproduce
    if (Plant.age[plant] < Plant.maturityAge[plant] || 
        Plant.energy[plant] < MIN_REPRODUCTION_ENERGY ||
        Plant.reproductionCooldown[plant] > 0) {
        return;
    }
    
    // Get chunk info
    const chunkKey = getChunkKey(Position.x[plant], Position.y[plant]);
    if (getPlantsInChunk(chunkKey) >= MAX_PLANTS_PER_CHUNK) {
        return; // Skip reproduction if chunk is full
    }
    
    // Consume energy for reproduction
    Plant.energy[plant] -= plantType.energyCost;
    
    // Reset cooldown (4x longer)
    Plant.reproductionCooldown[plant] = plantType.reproductionCooldown * 4;
    Plant.lastReproductionTime[plant] = world.tick;
    
    // Get parent plant's world position
    const parentX = Position.x[plant];
    const parentY = Position.y[plant];
    
    // Handle different reproduction methods with reduced counts
    switch (plantType.reproductionMethod) {
        case 'seed':
        case 'spore':
            // Create fewer seeds/spores
            const seedCount = Math.max(1, Math.floor(plantType.seedCount / 3)); // Even fewer seeds (was /2)
            for (let i = 0; i < seedCount; i++) {
                const pos = getWindInfluencedPosition(
                    parentX, 
                    parentY, 
                    plantType.seedSpreadRadius * TILE_SIZE
                );
                
                const tileX = Math.floor(pos.x / TILE_SIZE);
                const tileY = Math.floor(pos.y / TILE_SIZE);
                
                if (isValidPlantPosition(tileX, tileY, plantType)) {
                    const worldX = tileX * TILE_SIZE + TILE_SIZE/2;
                    const worldY = tileY * TILE_SIZE + TILE_SIZE/2;
                    spawnPlant(world, worldX, worldY, plantType.id, true);
                }
            }
            break;
            
        case 'clone':
            // Only 20% chance to actually try cloning
            if (Math.random() < 0.2) {
                const pos = getWindInfluencedPosition(
                    parentX,
                    parentY,
                    TILE_SIZE
                );
                
                const tileX = Math.floor(pos.x / TILE_SIZE);
                const tileY = Math.floor(pos.y / TILE_SIZE);
                
                if (isValidPlantPosition(tileX, tileY, plantType)) {
                    const worldX = tileX * TILE_SIZE + TILE_SIZE/2;
                    const worldY = tileY * TILE_SIZE + TILE_SIZE/2;
                    spawnPlant(world, worldX, worldY, plantType.id, true);
                }
            }
            break;
    }
}

// Update spawnPlant to track positions
function spawnPlant(world, x, y, typeId, isNewborn = false) {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);
    const posKey = `${tileX},${tileY}`;
    const chunkKey = getChunkKey(x, y);
    
    // Check if position is already occupied
    if (!plantPositions.has(chunkKey)) {
        plantPositions.set(chunkKey, new Set());
    }
    const chunkPositions = plantPositions.get(chunkKey);
    if (chunkPositions.has(posKey)) {
        return null;
    }
    
    const eid = addEntity(world);
    const plantType = getPlantType(typeId);
    if (!plantType) return null;
    
    // Add components
    addComponent(world, Plant, eid);
    addComponent(world, Position, eid);
    
    // Initialize plant properties
    Plant.type[eid] = typeId;
    Position.x[eid] = x;
    Position.y[eid] = y;
    Plant.x[eid] = tileX;
    Plant.y[eid] = tileY;
    Plant.maturityAge[eid] = plantType.maturityAge;
    
    if (isNewborn) {
        // New plants start small and need to grow
        Plant.growth[eid] = 0.1;
        Plant.age[eid] = 0;
        Plant.energy[eid] = MIN_REPRODUCTION_ENERGY;
        Plant.foodAmount[eid] = 0;
    } else {
        // Initial spawned plants get random ages and corresponding states
        const maxAge = plantType.maturityAge * 3; // Can be up to 3x maturity age
        Plant.age[eid] = Math.floor(Math.random() * maxAge);
        
        // Growth is full if mature, otherwise proportional to age
        const maturityProgress = Plant.age[eid] / plantType.maturityAge;
        Plant.growth[eid] = Math.min(1.0, maturityProgress);
        
        // Energy and food vary based on age and random factors
        Plant.energy[eid] = Math.min(100, 50 + Math.random() * 50 + (maturityProgress * 25));
        
        // For flowers, use nectar property instead of food
        if (typeId === PLANT_TYPES.FLOWER.id) {
            Plant.foodAmount[eid] = Math.floor(plantType.nectar * Plant.growth[eid] * (0.5 + Math.random() * 0.5));
            console.log(`[PLANTS] Spawned flower #${eid} with ${Math.round(Plant.foodAmount[eid])} nectar (growth: ${Math.round(Plant.growth[eid] * 100)}%)`);
        } else {
            Plant.foodAmount[eid] = Math.floor(plantType.maxFood * Plant.growth[eid] * (0.5 + Math.random() * 0.5));
        }
    }
    
    Plant.harvested[eid] = 0;
    Plant.reproductionCooldown[eid] = Math.floor(Math.random() * plantType.reproductionCooldown); // Random initial cooldown
    Plant.lastReproductionTime[eid] = 0;
    
    // Track the position
    chunkPositions.add(posKey);
    
    return eid;
}

// Helper function to remove plant position tracking
function removePlantPosition(x, y) {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);
    const posKey = `${tileX},${tileY}`;
    const chunkKey = getChunkKey(x, y);
    
    if (plantPositions.has(chunkKey)) {
        plantPositions.get(chunkKey).delete(posKey);
    }
}

export function plantGenerationSystem(world) {
    console.log('Starting plant generation...');
    let plantCount = {
        total: 0,
        byType: {}
    };

    // Initialize counts for each plant type
    Object.values(PLANT_TYPES).forEach(type => {
        plantCount.byType[type.name] = 0;
    });

    // Get all land tiles
    const landTiles = [];
    for (let x = 0; x < MAP_WIDTH; x++) {
        for (let y = 0; y < MAP_HEIGHT; y++) {
            const tile = tileManager.getTile(x, y);
            if (tile && tile.type === TILE_TYPES.LAND) {
                landTiles.push({ x, y, biome: tile.biome });
            }
        }
    }

    console.log(`Found ${landTiles.length} land tiles for potential plant spawns`);

    // Generate plants based on biome
    landTiles.forEach(tile => {
        // Get valid plant types for this biome
        const biomeId = tile.biome; // Numeric biome ID
        const biomeName = Object.keys(BIOME_TYPES).find(key => BIOME_TYPES[key] === biomeId); // Convert ID to name

        const validPlants = Object.values(PLANT_TYPES).filter(plant => 
            plant.biomes.includes(biomeName) // Check using the biome name
        );

        // Random chance to spawn a plant
        if (validPlants.length > 0 && Math.random() < 0.1) { // 10% chance per tile
            const plantType = validPlants[Math.floor(Math.random() * validPlants.length)];
            const worldX = tile.x * TILE_SIZE + TILE_SIZE/2;
            const worldY = tile.y * TILE_SIZE + TILE_SIZE/2;

            // Use spawnPlant with isNewborn=false to get random ages
            const plant = spawnPlant(world, worldX, worldY, plantType.id, false);
            
            if (plant !== null) {
                plantCount.total++;
                plantCount.byType[plantType.name] = (plantCount.byType[plantType.name] || 0) + 1;
            }
        }
    });

    // Log detailed plant generation results
    console.log('Plant generation complete. Summary:');
    console.log(`Total plants generated: ${plantCount.total}`);
    console.log('Plants by type:');
    Object.entries(plantCount.byType).forEach(([type, count]) => {
        console.log(`  ${type}: ${count}`);
    });

    return world;
}

// Helper function to get active plants based on player position
function getActivePlants(world, plants) {
    const playerQuery = defineQuery([Position]);
    const players = playerQuery(world);
    if (players.length === 0) return [];
    
    const player = players[0];
    const playerChunkX = Math.floor(Position.x[player] / (CHUNK_SIZE * TILE_SIZE));
    const playerChunkY = Math.floor(Position.y[player] / (CHUNK_SIZE * TILE_SIZE));
    
    // Filter plants to only those within active radius
    return plants.filter(plant => {
        const plantChunkX = Math.floor(Position.x[plant] / (CHUNK_SIZE * TILE_SIZE));
        const plantChunkY = Math.floor(Position.y[plant] / (CHUNK_SIZE * TILE_SIZE));
        
        const dx = Math.abs(plantChunkX - playerChunkX);
        const dy = Math.abs(plantChunkY - playerChunkY);
        
        return dx <= ACTIVE_RADIUS && dy <= ACTIVE_RADIUS;
    });
}

// Update plant growth system to run once per day
export function plantGrowthSystem(world) {
    // Get time entity
    const timeEntities = timeQuery(world);
    if (timeEntities.length === 0) return world;
    const timeEntity = timeEntities[0];
    
    // Only process at the start of each day (when ticks reset to 0)
    if (Time.ticks[timeEntity] !== 0) return world;
    
    const plants = plantQuery(world);
    console.log(`[PLANTS] Processing ${plants.length} plants for growth`);
    
    // Process ALL plants
    for (const plant of plants) {
        // Update age in days
        Plant.age[plant] = (Plant.age[plant] || 0) + 1;
        
        const plantType = getPlantType(Plant.type[plant]);
        if (!plantType) continue;
        
        // Handle growth
        if (Plant.growth[plant] < 1.0) {
            // Grow the plant
            Plant.growth[plant] += GROWTH_RATE;
            
            // Update food amount based on growth
            if (Plant.type[plant] === PLANT_TYPES.FLOWER.id) {
                Plant.foodAmount[plant] = plantType.nectar * Plant.growth[plant];
                console.log(`[PLANTS] Flower #${plant} grew to ${Math.round(Plant.growth[plant] * 100)}%. Nectar: ${Math.round(Plant.foodAmount[plant])}/${plantType.nectar}`);
            } else {
                Plant.foodAmount[plant] = plantType.maxFood * Plant.growth[plant];
            }
        }
        
        // Generate energy
        if (Plant.energy[plant] < 100) {
            Plant.energy[plant] = Math.min(100, Plant.energy[plant] + ENERGY_GAIN_RATE);
        }
        
        // Handle reproduction cooldown (in days)
        if (Plant.reproductionCooldown[plant] > 0) {
            Plant.reproductionCooldown[plant]--;
        }
        
        // Try reproduction with much reduced chance
        if (Math.random() < 0.02) { // 2% chance to attempt reproduction
            tryReproduction(world, plant);
        }
        
        // Handle harvested plants
        if (Plant.harvested[plant]) {
            // Regrow harvested plants
            Plant.growth[plant] += REGROWTH_MULTIPLIER / plantType.regrowthTime;
            
            if (Plant.growth[plant] >= 1) {
                Plant.harvested[plant] = 0;
                Plant.growth[plant] = 1;
                Plant.foodAmount[plant] = plantType.maxFood;
            }
        }
    }
    
    return world;
}

function generatePlants(world) {
    console.log('Starting plant generation...');
    let plantCount = {
        total: 0,
        byType: {}
    };

    // Initialize counts for each plant type
    Object.values(PLANT_TYPES).forEach(type => {
        plantCount.byType[type.name] = 0;
    });

    // Get all land tiles
    const landTiles = [];
    for (let x = 0; x < MAP_WIDTH; x++) {
        for (let y = 0; y < MAP_HEIGHT; y++) {
            const tile = tileManager.getTile(x, y);
            if (tile && tile.type === TILE_TYPES.LAND) {
                landTiles.push({ x, y, biome: tile.biome });
            }
        }
    }

    console.log(`Found ${landTiles.length} land tiles for potential plant spawns`);

    // Generate plants based on biome
    landTiles.forEach(tile => {
        const biomeType = Object.keys(BIOME_TYPES)[tile.biome];
        
        // Get valid plant types for this biome
        const validPlants = Object.values(PLANT_TYPES).filter(plant => 
            plant.biomes.includes(biomeType)
        );

        // Random chance to spawn a plant
        if (validPlants.length > 0 && Math.random() < 0.1) { // 10% chance per tile
            const plantType = validPlants[Math.floor(Math.random() * validPlants.length)];
            const worldX = tile.x * TILE_SIZE + TILE_SIZE/2;
            const worldY = tile.y * TILE_SIZE + TILE_SIZE/2;

            const plant = addComponent(addEntity(world), Plant);
            Plant.type[plant] = plantType.id;
            Plant.x[plant] = worldX;
            Plant.y[plant] = worldY;
            Plant.growth[plant] = Math.random();
            Plant.foodAmount[plant] = Math.floor(Math.random() * plantType.maxFood);
            Plant.harvested[plant] = false;

            plantCount.total++;
            plantCount.byType[plantType.name]++;
        }
    });

    // Log detailed plant generation results
    console.log('Plant generation complete. Summary:');
    console.log(`Total plants generated: ${plantCount.total}`);
    console.log('Plants by type:');
    Object.entries(plantCount.byType).forEach(([type, count]) => {
        console.log(`  ${type}: ${count}`);
    });

    return world;
} 